import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { AppTableAction, AuthPermissions } from '../../../_models/models';
import {
  ROW_ACTION_COLOR_CLASS,
  ROW_ACTION_DANGER_COLOR_CLASS,
  RowActionBehavior,
  RowActionContext,
  RowActionItem,
  INLINE_ACTIONS_THRESHOLD,
  resolveRowActionBehavior
} from './table-row-actions.config';

@Component({
  selector: 'app-table-row-actions',
  templateUrl: './table-row-actions.component.html',
  styleUrls: ['./table-row-actions.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableRowActionsComponent implements OnChanges {
  @Input() actions: AppTableAction[] = [];
  @Input() row: any;
  @Input() idProp: string;
  @Input() tableId: string;
  @Input() authPermissions: AuthPermissions;
  @Input() isExternal = false;

  @Output() actionSelected: EventEmitter<AppTableAction> = new EventEmitter<AppTableAction>();

  @ViewChild('trigger') private trigger: ElementRef<HTMLButtonElement>;

  isMenuOpen = false;
  inlineItems: RowActionItem[] = [];
  menuItems: RowActionItem[] = [];

  constructor(@Inject(DOCUMENT) private document: Document) {}

  get triggerId(): string {
    return `${this.tableId}-more-actions-click-${this.row?.[this.idProp]}`;
  }

  get menuId(): string {
    return `${this.tableId}-row-actions-menu-${this.row?.[this.idProp]}`;
  }

  ngOnChanges(): void {
    const context: RowActionContext = { row: this.row, authPermissions: this.authPermissions, isExternal: this.isExternal };
    const items = (this.actions || [])
      .filter(action => resolveRowActionBehavior(action).isVisible(action, context))
      .map(action => this.toItem(action, context));
    const promoted = this.resolvePromotedItems(items);
    this.inlineItems = promoted;
    this.menuItems = items.filter(item => !promoted.includes(item));
  }

  private resolvePromotedItems(items: RowActionItem[]): RowActionItem[] {
    return items.filter(item => !item.isDanger).slice(0, INLINE_ACTIONS_THRESHOLD);
  }

  trackByElementId(index: number, item: RowActionItem): string {
    return item.elementId;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  onSelect(item: RowActionItem): void {
    this.closeMenu();
    if (!item.isDisabled) {
      this.actionSelected.emit(item.action);
    }
  }

  onMenuVisibleChange(isOpen: boolean): void {
    this.isMenuOpen = isOpen;
    if (isOpen) {
      setTimeout(() => this.focusItemAt(0));
    }
  }

  activateFocusedItem(event: Event): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).click();
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Tab') {
      this.closeMenu();
      this.trigger?.nativeElement.focus();
      return;
    }
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    if (step) {
      event.preventDefault();
      this.moveFocus(step);
    }
  }

  private get menuItemElements(): HTMLElement[] {
    const menu = this.document.getElementById(this.menuId);
    return menu ? Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]')) : [];
  }

  private focusItemAt(index: number): void {
    this.menuItemElements[index]?.focus();
  }

  private moveFocus(step: number): void {
    const elements = this.menuItemElements;
    if (!elements.length) {
      return;
    }
    const current = elements.indexOf(this.document.activeElement as HTMLElement);
    const next = current === -1 ? (step > 0 ? 0 : elements.length - 1) : (current + step + elements.length) % elements.length;
    this.focusItemAt(next);
  }

  private toItem(action: AppTableAction, context: RowActionContext): RowActionItem {
    const behavior = resolveRowActionBehavior(action);
    const isDisabled = !!behavior.isDisabled?.(context);
    const isDanger = !!behavior.isDanger;
    return {
      action,
      kind: this.resolveKind(action, behavior),
      label: action.displayName,
      icon: (action.icon as IconProp) || behavior.icon,
      isDisabled,
      tooltip: isDisabled ? behavior.disabledTooltip?.(context) : null,
      elementId: `${this.tableId}-${action.name}-${this.row?.[this.idProp]}`,
      routerLink: action.hrefLink ? `${this.row?.[action.hrefIdProp]}${action.hrefLink}` : null,
      confirmOkText: behavior.confirmOkText,
      confirmDescription: action.desc,
      isDanger,
      colorClass: isDanger ? ROW_ACTION_DANGER_COLOR_CLASS : ROW_ACTION_COLOR_CLASS
    };
  }

  private resolveKind(action: AppTableAction, behavior: RowActionBehavior): RowActionItem['kind'] {
    if (behavior.confirmOkText) {
      return 'confirm';
    }
    return action.hrefLink ? 'link' : 'click';
  }
}
